import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Calendar, Loader2, Pause, Play, RefreshCw, Zap } from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

interface SchedulerJob {
  name: string;
  schedule: string;
  timeZone: string;
  state: string;
  lastAttemptTime: string | null;
  nextScheduleTime: string | null;
  description: string;
  targetUri: string | null;
}

interface SchedulerJobsResponse {
  jobs: SchedulerJob[];
}

type Action = 'pause' | 'resume' | 'run';

/* ─── Helpers ─────────────────────────────────── */

function fmt(ts: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  const now = Date.now();
  const diffSec = Math.floor((now - d.getTime()) / 1000);
  if (Math.abs(diffSec) < 60) {
    return diffSec >= 0 ? `${diffSec}s ago` : `${Math.abs(diffSec)}s from now`;
  }
  return d.toLocaleString();
}

function StateBadge({ state }: { state: string }) {
  if (state === 'ENABLED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
        enabled
      </span>
    );
  }
  if (state === 'PAUSED') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      {state.toLowerCase()}
    </span>
  );
}

/* ─── Main Component ──────────────────────────── */

export function SchedulerPage() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery<SchedulerJob[], Error>({
    queryKey: ['admin', 'scheduler', 'jobs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<SchedulerJobsResponse>(
        '/api/v1/ingestion/admin/scheduler/jobs',
        { signal },
      );
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Failed to load scheduler jobs');
      }
      return res.data.jobs ?? [];
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const action = useMutation({
    mutationFn: async ({ name, op }: { name: string; op: Action }) => {
      setBusy(`${name}:${op}`);
      const res = await api.post(
        `/api/v1/ingestion/admin/scheduler/jobs/${encodeURIComponent(name)}/${op}`,
        {},
      );
      if (!res.success) throw new Error(res.error ?? `${op} failed`);
    },
    onSettled: () => {
      setBusy(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'scheduler'] });
    },
  });

  const jobs = data ?? [];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Cloud Scheduler
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Pause, resume, or trigger Cloud Scheduler jobs that drive ingestion cron workloads.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            auto 30s
          </span>
          <button
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
          >
            {isFetching ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-red-200 dark:border-red-900/40 p-4 text-sm text-red-600 dark:text-red-400">
          Failed to load: {error.message}
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <Calendar className="w-10 h-10 text-gray-400 dark:text-gray-500 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            No scheduler jobs.
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Apply <code className="font-mono">deploy/scheduler/jobs.yaml</code> to provision jobs.
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    Job
                  </th>
                  <th className="text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    Schedule
                  </th>
                  <th className="text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    State
                  </th>
                  <th className="text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    Last run
                  </th>
                  <th className="text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    Next run
                  </th>
                  <th className="text-right font-medium px-3 py-2 text-[10px] uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {jobs.map((j) => {
                  const pauseBusy = busy === `${j.name}:pause`;
                  const resumeBusy = busy === `${j.name}:resume`;
                  const runBusy = busy === `${j.name}:run`;
                  const isEnabled = j.state === 'ENABLED';
                  return (
                    <tr key={j.name} className="text-gray-700 dark:text-gray-200">
                      <td className="px-3 py-2 align-top">
                        <div className="font-mono text-xs text-gray-900 dark:text-gray-100">
                          {j.name}
                        </div>
                        {j.description ? (
                          <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                            {j.description}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="font-mono text-xs text-gray-700 dark:text-gray-200">
                          {j.schedule}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">
                          ({j.timeZone})
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <StateBadge state={j.state} />
                      </td>
                      <td className="px-3 py-2 align-top text-gray-600 dark:text-gray-300">
                        {fmt(j.lastAttemptTime)}
                      </td>
                      <td className="px-3 py-2 align-top text-gray-600 dark:text-gray-300">
                        {fmt(j.nextScheduleTime)}
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="inline-flex items-center gap-1.5">
                          {isEnabled ? (
                            <button
                              onClick={() => action.mutate({ name: j.name, op: 'pause' })}
                              disabled={pauseBusy || runBusy}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium border border-amber-200 dark:border-amber-900/40 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-50 dark:hover:bg-amber-950/40 disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Pause this scheduler job"
                            >
                              {pauseBusy ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Pause className="w-3 h-3" />
                              )}
                              Pause
                            </button>
                          ) : (
                            <button
                              onClick={() => action.mutate({ name: j.name, op: 'resume' })}
                              disabled={resumeBusy || runBusy}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium border border-green-200 dark:border-green-900/40 text-green-700 dark:text-green-300 rounded hover:bg-green-50 dark:hover:bg-green-950/40 disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Resume this scheduler job"
                            >
                              {resumeBusy ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Play className="w-3 h-3" />
                              )}
                              Resume
                            </button>
                          )}
                          <button
                            onClick={() => action.mutate({ name: j.name, op: 'run' })}
                            disabled={runBusy || pauseBusy || resumeBusy}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Trigger this job immediately"
                          >
                            {runBusy ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Zap className="w-3 h-3" />
                            )}
                            Run now
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
